import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { publishAuthStep } from '@nbx/frontend-helpers/publish';
import { Context } from 'state/store';
import { setOverlay, setNavToggleRef } from 'state/creators';
import isLinkActive from 'helpers/isLinkActive';
import { useIsTablet } from 'helpers/hooks';
import OVERLAYS from 'constants/overlays';
import styles from './styles.module.scss';

const microapps = window.MICROFRONTENDS_CONFIG;
function HorizontalNavigation(props) {
  const [state, dispatch] = React.useContext(Context);
  const { t } = useTranslation();
  const location = useLocation();
  const toggleRef = React.useRef(null);
  const isTablet = useIsTablet();

  // set Refs
  React.useEffect(() => {
    dispatch(setNavToggleRef(toggleRef.current));
    return () => dispatch(setNavToggleRef(null));
  }, [dispatch, props.isTradingPage, isTablet]);

  const resetAuthStep = () => publishAuthStep(null);
  const toggleNav = () =>
    state.overlay !== OVERLAYS.nav ? dispatch(setOverlay(OVERLAYS.nav)) : dispatch(setOverlay(null));

  function renderNavLink({ to, paths, label, href, onClick, isForLoggedOut = false, transparentBG = false }) {
    return (
      <nbx-button-text
        to={to}
        navigation={!isForLoggedOut}
        navigation-logged-out={isForLoggedOut}
        navigation-active={transparentBG || (to && paths && isLinkActive(location, paths))}
        onClick={onClick}
        href={href}>
        {label}
      </nbx-button-text>
    );
  }

  function renderNavToggle() {
    if (!isTablet && !props.isTradingPage) return null;
    return (
      <button type="button" className={styles.navToggle} onClick={toggleNav} ref={toggleRef}>
        {state.overlay === OVERLAYS.nav ? <nbx-icon icon="close_v2" /> : <nbx-icon icon="menu_v2" />}
      </button>
    );
  }

  function renderLoggedInNav() {
    return (
      <div className={styles.leftContainer}>
        {renderNavToggle()}
        <Link to="/dashboards">
          <nbx-icon icon="nbx_logo" />
        </Link>
      </div>
    );
  }

  function renderLoggedOutNav() {
    return (
      <>
        <div className={styles.leftContainer}>
          <Link to="/">
            <nbx-icon icon="nbx_logo" />
          </Link>
        </div>

        <div className={styles.headerLinksWrap}>
          {renderNavLink({
            to: microapps?.routes?.nft?.path,
            paths: [...window.GET_LOCALE_PATHS(microapps?.routes?.nft?.path)],
            label: t('header_link_nft'),
            isForLoggedOut: true,
            transparentBG: props.isTransparent
          })}

          {renderNavLink({
            to: microapps?.routes?.markets?.path,
            paths: [
              ...window.GET_LOCALE_PATHS(microapps?.routes?.markets?.path),
              ...window.GET_LOCALE_PATHS(microapps?.routes?.trading?.path)
            ],
            label: t('header_link_markets'),
            isForLoggedOut: true,
            transparentBG: props.isTransparent
          })}

          {renderNavLink({
            onClick: () => dispatch(setOverlay(OVERLAYS.otc)),
            label: t('header_link_otc'),
            isForLoggedOut: true,
            transparentBG: props.isTransparent
          })}

          {renderNavLink({
            href: 'https://nbxsupport.zendesk.com/hc/en-us',
            label: t('common_support'),
            isForLoggedOut: true,
            transparentBG: props.isTransparent
          })}

          <nbx-button to={microapps?.routes?.signup?.path} onClick={resetAuthStep}>
            {t('common_sign_up')}
          </nbx-button>

          <nbx-button to={microapps?.routes?.login?.path} onClick={resetAuthStep}>
            {t('common_log_in')}
          </nbx-button>
        </div>
      </>
    );
  }

  if (state.activeAccount) return renderLoggedInNav();
  return renderLoggedOutNav();
}

HorizontalNavigation.propTypes = {
  isTransparent: PropTypes.bool,
  isTradingPage: PropTypes.bool
};

HorizontalNavigation.defaultProps = {
  isTransparent: false,
  isTradingPage: false
};

export default HorizontalNavigation;
